import React from 'react';
import { CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/Button';
import SectionWrapper from './SectionWrapper';

const PricingSection: React.FC = () => {
  return (
    <SectionWrapper className="!bg-white">
      {/* Atmospheric lighting effects */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary-500/15 blur-[100px] -z-10"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-blue-500/15 blur-[100px] -z-10"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12 text-gray-950">
          Compare our straightforward <span className="gradient-text">approach</span>
        </h2>
        
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mt-16">
          {/* Other Journals */}
          <div className="glass-card p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow hover-card">
            <h3 className="text-xl font-display font-semibold mb-2 text-gray-900">Other Journals</h3>
            <p className="text-3xl font-display font-bold text-red-600 mb-6">≈$ expensive </p>
            <div className="space-y-4">
              <div className="flex items-start">
                <XCircle className="text-red-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">Overwhelming interface</p>
              </div>
              <div className="flex items-start">
                <XCircle className="text-red-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">Too many unnecessary features</p>
              </div>
               <div className="flex items-start">
                <XCircle className="text-red-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">No discipline tracking</p>
              </div>
              <div className="flex items-start">
                <XCircle className="text-red-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">Complex setup process</p>
              </div>
              <div className="flex items-start">
                <XCircle className="text-red-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">Feels like work</p>
              </div>
              <div className="flex items-start">
                <XCircle className="text-red-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">Expensive monthly fees</p>
              </div>
            </div>
          </div>
          
          {/* Our Journal */}
          <div className="relative">
            <Link 
              to="/trial" 
              className="glass-card p-8 rounded-xl border-2 border-primary-400 transform scale-105 relative transition-all duration-300 hover:scale-[1.07] hover:shadow-glow shadow-lg cursor-pointer block"
            >
            
            <h3 className="text-xl font-display font-semibold mb-2 text-gray-900">Simplified Trading Journal</h3>
            <div className="mb-6 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-emerald-100 to-emerald-50 rounded-lg blur-lg opacity-75"></div>
              <div className="relative">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-4xl font-display font-bold text-emerald-600">$8.33</span>
                  <span className="text-lg text-emerald-600">/month</span>
                </div>
                <p className="text-sm font-medium text-emerald-500">Less than a coffee - with annual billing!</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start">
                <CheckCircle className="text-emerald-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">Journal in Seconds</p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-emerald-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">Track your trading discipline</p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-emerald-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">Build green discipline momentum</p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-emerald-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">AI Trading Coach</p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-emerald-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">Clean, intuitive interface</p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-emerald-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">Focus on essential metrics</p>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-emerald-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">Affordable pricing</p>
              </div>
            </div>
            <div className="mt-8 space-y-3">
              <Button 
                as={Link} 
                to="/trial"
                variant="primary"
                fullWidth
                className="flex items-center justify-center px-6 py-3 bg-black hover:bg-gray-900 text-white font-medium !rounded-full shadow-md hover:shadow-lg transition-all duration-300"
              >
                Free Trial
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </div>
            </Link>
          </div>
          
          {/* Spreadsheets */}
          <div className="glass-card p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow hover-card">
            <h3 className="text-xl font-display font-semibold mb-2 text-gray-900">Spreadsheets</h3>
            <p className="text-3xl font-display font-bold text-gray-400 mb-6">Free but limited</p>
            <div className="space-y-4">
              <div className="flex items-start">
                <XCircle className="text-red-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">Definitely feels like work</p>
              </div>
              <div className="flex items-start">
                <XCircle className="text-red-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">No visual analytics</p>
              </div>
              <div className="flex items-start">
                <XCircle className="text-red-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">Easy to break formulas</p>
              </div>
              <div className="flex items-start">
                <XCircle className="text-red-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">No trade screenshots</p>
              </div>
              <div className="flex items-start">
                <XCircle className="text-red-500 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-gray-700">Time-consuming maintenance</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export default PricingSection;